import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ProviderState, ProviderFilter, CreateProviderInput, UpdateProviderInput } from './types';
import { createProvider as createProviderService } from '@/services/providerService';
import { fetchProviders as fetchProvidersService } from '@/services/providerService';
import { fetchMyProviders as fetchMyProvidersService } from '@/services/providerService';
import { updateProviderService } from '@/services/providerService';

// Initial state
const initialState: ProviderState = {
  providers: [],
  currentProvider: null,
  loading: false,
  error: null,
  filter: {
    page: 1,
    limit: 9
  },
  paginationMeta: {
    totalItems: 0,
    itemCount: 0,
    itemsPerPage: 9,
    totalPages: 0,
    currentPage: 1
  }
};

// Async thunks
export const createProvider = createAsyncThunk(
  'provider/createProvider',
  async ({ input, logo }: { input: CreateProviderInput, logo: File | null }, { rejectWithValue }) => {
    try {
      return await createProviderService(input, logo);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create provider');
    }
  }
);

// Create async thunk for fetching providers
export const fetchFilteredProviders = createAsyncThunk(
  'provider/fetchFilteredProviders',
  async (filter: ProviderFilter, { rejectWithValue }) => {
    try {
      const response = await fetchProvidersService(filter);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch providers');
    }
  }
);

// Create async thunk for fetching user's providers
export const fetchMyProviders = createAsyncThunk(
  'provider/fetchMyProviders',
  async (filter: ProviderFilter, { rejectWithValue }) => {
    try {
      const response = await fetchMyProvidersService(filter);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch my providers');
    }
  }
);

// Create the slice
const providerSlice = createSlice({
  name: 'provider',
  initialState,
  reducers: {
    setProviderFilter: (state, action: PayloadAction<Partial<ProviderFilter>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    clearCurrentProvider: (state) => {
      state.currentProvider = null;
    },
    resetProviderError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProvider = action.payload;
      })
      .addCase(createProvider.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch filtered providers
    builder.addCase(fetchFilteredProviders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchFilteredProviders.fulfilled, (state, action) => {
      state.loading = false;
      state.providers = action.payload.providers;
      state.paginationMeta = action.payload.meta;
    });
    builder.addCase(fetchFilteredProviders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch my providers
    builder.addCase(fetchMyProviders.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMyProviders.fulfilled, (state, action) => {
      state.loading = false;
      state.providers = action.payload.providers;
      state.paginationMeta = action.payload.meta;
    });
    builder.addCase(fetchMyProviders.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  }
});

// Export actions
export const {
  setProviderFilter,
  clearCurrentProvider,
  resetProviderError
} = providerSlice.actions;

// Export reducer
export default providerSlice.reducer;

export const updateProvider = createAsyncThunk(
  'provider/update',
  async ({ input, logo, images }: { input: UpdateProviderInput, logo: File | null, images: File[] }, { rejectWithValue }) => {
    try {
      // Ensure the ID is correctly passed as a number
      if (!input.id) {
        return rejectWithValue('Provider ID is required for update operation');
      }

      // Add debug logging
      console.log('Updating provider with input:', JSON.stringify({
        ...input,
        id: input.id // Explicitly log the ID
      }));

      const result = await updateProviderService(input, logo, images);

      // Check if there's a warning about creating instead of updating
      if (result._warning) {
        console.warn(result._warning);
      }

      return result;
    } catch (error: any) {
      // Improved error handling
      console.error('Provider update error in slice:', error);
      return rejectWithValue(error.message || 'Failed to update provider');
    }
  }
);